import React, { useState, useEffect } from 'react';
import { Lock, RefreshCw, Mail, Building2, Tag, Check, Trash2, MessageSquare, Undo2, ArrowRightCircle } from 'lucide-react';

const AccessRequestsAdmin = () => {
  const [password, setPassword] = useState('');
  const [authed, setAuthed] = useState(false);
  const [token, setToken] = useState('');
  const [requests, setRequests] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [tab, setTab] = useState('requests');
  const [showAcked, setShowAcked] = useState(false);
  const [editingNotes, setEditingNotes] = useState(null);
  const [noteText, setNoteText] = useState('');

  const handleLogin = (e) => {
    e.preventDefault();
    setToken(password);
    setAuthed(true);
  };

  const fetchRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/admin/requests', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unauthorized');
      setRequests(data.data?.requests || []);
      setVouchers(data.data?.vouchers || []);
    } catch (err) {
      setError(err.message);
      if (err.message === 'Unauthorized') setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const apiAction = async (method, body) => {
    try {
      const res = await fetch('/api/admin/requests', {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed');
      await fetchRequests();
    } catch (err) {
      setError(err.message);
    }
  };

  const acknowledge = (id) => apiAction('PUT', { id, action: 'acknowledge' });
  const unacknowledge = (id) => apiAction('PUT', { id, action: 'unacknowledge' });
  const saveNotes = (id, table) => { apiAction('PUT', { id, action: 'notes', notes: noteText, table }); setEditingNotes(null); };
  const deleteRequest = (id, table) => { if (confirm('Delete this entry?')) apiAction('DELETE', { id, table }); };
  const moveToRequests = (id) => apiAction('PUT', { id, action: 'move_to_requests' });

  useEffect(() => {
    if (authed && token) fetchRequests();
  }, [authed, token]);

  const filteredRequests = (filter
    ? requests.filter((r) =>
        [r.name, r.email, r.organization, r.voucher_code_attempted, r.notes].some(
          (v) => v && v.toLowerCase().includes(filter.toLowerCase())
        )
      )
    : requests
  ).filter((r) => showAcked || !r.acknowledged_at);

  const filteredVouchers = filter
    ? vouchers.filter((v) =>
        [v.contact_name, v.contact_email, v.organization, v.voucher_id].some(
          (val) => val && val.toLowerCase().includes(filter.toLowerCase())
        )
      )
    : vouchers;

  const ackedCount = requests.filter((r) => r.acknowledged_at).length;

  if (!authed) {
    return (
      <div className="fos-notebook min-h-screen flex items-center justify-center px-5">
        <div className="nb-sketch" style={{ maxWidth: 380, width: '100%' }}>
          <div className="text-center mb-6">
            <Lock size={28} style={{ color: 'var(--ink-3)', margin: '0 auto 12px' }} />
            <h1 style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 24, color: 'var(--ink)' }}>Admin Access</h1>
            <p style={{ fontFamily: 'var(--hand)', fontSize: 14, color: 'var(--ink-3)', marginTop: 4 }}>Enter your admin secret to view requests</p>
          </div>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin secret"
              required
              style={{
                width: '100%', padding: '10px 14px', background: 'var(--paper-2)',
                border: '1.25px solid var(--ink-4)', borderRadius: 3,
                fontFamily: 'var(--mono)', fontSize: 14, color: 'var(--ink)', outline: 'none',
                marginBottom: 12,
              }}
            />
            <button type="submit" className="nb-btn w-full">Sign in</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="fos-notebook min-h-screen">
      <div className="px-5 py-4" style={{ borderBottom: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[1080px] mx-auto flex items-center justify-between">
          <div>
            <h1 style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 20, color: 'var(--ink)' }}>Access Requests</h1>
            <p style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)', letterSpacing: '0.4px' }}>
              {requests.length} requests ({ackedCount} acknowledged) · {vouchers.length} vouchers
            </p>
          </div>
          <button
            onClick={fetchRequests}
            disabled={loading}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', opacity: loading ? 0.5 : 1 }}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="max-w-[1080px] mx-auto px-5 py-6">
        {error && (
          <div className="nb-sticky mb-5" style={{ transform: 'none', maxWidth: '100%' }}>
            <b>Error:</b> {error}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-4 mb-5">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search..."
            style={{
              width: '100%', maxWidth: 300, padding: '8px 14px',
              background: 'var(--paper-2)', border: '1.25px solid var(--ink-4)',
              borderRadius: 3, fontFamily: 'var(--hand)', fontSize: 15,
              color: 'var(--ink)', outline: 'none',
            }}
          />
          {tab === 'requests' && (
            <label className="flex items-center gap-2" style={{ fontFamily: 'var(--hand)', fontSize: 14, color: 'var(--ink-3)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showAcked}
                onChange={(e) => setShowAcked(e.target.checked)}
                style={{ accentColor: 'var(--nb-accent)' }}
              />
              Show acknowledged ({ackedCount})
            </label>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-5" style={{ borderBottom: '1px dashed var(--ink-4)' }}>
          <button
            onClick={() => setTab('requests')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', paddingBottom: 8,
              fontFamily: 'var(--hand)', fontSize: 16, fontWeight: tab === 'requests' ? 700 : 400,
              color: tab === 'requests' ? 'var(--ink)' : 'var(--ink-3)',
              borderBottom: tab === 'requests' ? '2px solid var(--nb-accent)' : '2px solid transparent',
            }}
          >
            Requests ({requests.length - ackedCount} new)
          </button>
          <button
            onClick={() => setTab('vouchers')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', paddingBottom: 8,
              fontFamily: 'var(--hand)', fontSize: 16, fontWeight: tab === 'vouchers' ? 700 : 400,
              color: tab === 'vouchers' ? 'var(--ink)' : 'var(--ink-3)',
              borderBottom: tab === 'vouchers' ? '2px solid var(--nb-accent)' : '2px solid transparent',
            }}
          >
            Vouchers ({vouchers.length})
          </button>
        </div>

        {/* Requests tab */}
        {tab === 'requests' && (
          <div className="space-y-3">
            {filteredRequests.map((r, i) => (
              <div
                key={r.id || i}
                className="nb-sketch"
                style={{ padding: '14px 18px', opacity: r.acknowledged_at ? 0.55 : 1 }}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      {r.acknowledged_at && (
                        <Check size={14} style={{ color: 'var(--nb-green)' }} />
                      )}
                      <span style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 17, color: 'var(--ink)' }}>
                        {r.name}
                      </span>
                      {r.role && (
                        <span style={{ fontFamily: 'var(--hand)', fontSize: 14, color: 'var(--ink-3)' }}>— {r.role}</span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-4 mt-1">
                      <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 no-underline" style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink-2)' }}>
                        <Mail size={11} style={{ color: 'var(--ink-3)' }} />
                        {r.email}
                      </a>
                      {r.organization && (
                        <span className="flex items-center gap-1.5" style={{ fontFamily: 'var(--hand)', fontSize: 13, color: 'var(--ink-3)' }}>
                          <Building2 size={11} />
                          {r.organization}{r.team_name ? ` · ${r.team_name}` : ''}
                        </span>
                      )}
                      {r.phone && (
                        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)' }}>{r.phone}</span>
                      )}
                    </div>

                    {(r.secondary_name || r.secondary_email) && (
                      <div className="mt-2" style={{ fontFamily: 'var(--hand)', fontSize: 13, color: 'var(--ink-4)' }}>
                        2nd: {r.secondary_name}{r.secondary_role ? ` (${r.secondary_role})` : ''}{r.secondary_email ? ` · ${r.secondary_email}` : ''}
                      </div>
                    )}

                    {r.voucher_code_attempted && (
                      <div className="mt-1 flex items-center gap-1.5">
                        <Tag size={11} style={{ color: 'var(--nb-accent)' }} />
                        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--nb-accent)' }}>Voucher: {r.voucher_code_attempted}</span>
                      </div>
                    )}

                    {/* Notes display/edit */}
                    {editingNotes === r.id ? (
                      <div className="mt-2 flex gap-2">
                        <input
                          type="text"
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          placeholder="Add a note..."
                          autoFocus
                          onKeyDown={(e) => e.key === 'Enter' && saveNotes(r.id)}
                          style={{
                            flex: 1, padding: '6px 10px', background: 'var(--paper-2)',
                            border: '1.25px solid var(--ink-4)', borderRadius: 3,
                            fontFamily: 'var(--hand)', fontSize: 13, color: 'var(--ink)', outline: 'none',
                          }}
                        />
                        <button onClick={() => saveNotes(r.id)} className="nb-chip" style={{ cursor: 'pointer', borderColor: 'var(--nb-green)', color: 'var(--nb-green)' }}>save</button>
                        <button onClick={() => setEditingNotes(null)} className="nb-chip" style={{ cursor: 'pointer' }}>cancel</button>
                      </div>
                    ) : r.notes ? (
                      <div className="mt-2 flex items-start gap-1.5 cursor-pointer" onClick={() => { setEditingNotes(r.id); setNoteText(r.notes || ''); }}>
                        <MessageSquare size={11} style={{ color: 'var(--ink-4)', marginTop: 2 }} />
                        <span style={{ fontFamily: 'var(--hand)', fontSize: 13, color: 'var(--ink-3)', fontStyle: 'italic' }}>{r.notes}</span>
                      </div>
                    ) : null}
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--ink-4)', whiteSpace: 'nowrap' }}>
                      {r.created_at ? new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                    </div>
                    <div className="flex gap-1">
                      {r.acknowledged_at ? (
                        <button onClick={() => unacknowledge(r.id)} title="Mark as new" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', padding: 4 }}>
                          <Undo2 size={13} />
                        </button>
                      ) : (
                        <button onClick={() => acknowledge(r.id)} title="Acknowledge" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nb-green)', padding: 4 }}>
                          <Check size={13} />
                        </button>
                      )}
                      <button onClick={() => { setEditingNotes(r.id); setNoteText(r.notes || ''); }} title="Add note" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', padding: 4 }}>
                        <MessageSquare size={13} />
                      </button>
                      <button onClick={() => deleteRequest(r.id)} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', padding: 4 }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {filteredRequests.length === 0 && !loading && (
              <div className="text-center py-12">
                <p style={{ fontFamily: 'var(--hand)', fontSize: 16, color: 'var(--ink-3)' }}>
                  {filter ? 'No matching requests' : showAcked ? 'No requests yet' : 'All caught up — no new requests'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Vouchers tab */}
        {tab === 'vouchers' && (
          <div className="space-y-3">
            {filteredVouchers.map((v, i) => (
              <div key={v.id || i} className="nb-sketch" style={{ padding: '14px 18px' }}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="nb-chip" style={{ borderColor: 'var(--nb-green)', color: 'var(--nb-green)', background: 'rgba(61,107,52,0.10)' }}>
                        {v.voucher_id}
                      </span>
                      <span style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 17, color: 'var(--ink)' }}>
                        {v.contact_name || '—'}
                      </span>
                      {v.contact_role && (
                        <span style={{ fontFamily: 'var(--hand)', fontSize: 14, color: 'var(--ink-3)' }}>— {v.contact_role}</span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-4 mt-1">
                      {v.contact_email && (
                        <a href={`mailto:${v.contact_email}`} className="flex items-center gap-1.5 no-underline" style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink-2)' }}>
                          <Mail size={11} style={{ color: 'var(--ink-3)' }} />
                          {v.contact_email}
                        </a>
                      )}
                      {v.organization && (
                        <span className="flex items-center gap-1.5" style={{ fontFamily: 'var(--hand)', fontSize: 13, color: 'var(--ink-3)' }}>
                          <Building2 size={11} />
                          {v.organization}{v.team_name ? ` · ${v.team_name}` : ''}
                        </span>
                      )}
                      {v.contact_phone && (
                        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)' }}>{v.contact_phone}</span>
                      )}
                    </div>

                    {(v.secondary_name || v.secondary_email) && (
                      <div className="mt-2" style={{ fontFamily: 'var(--hand)', fontSize: 13, color: 'var(--ink-4)' }}>
                        2nd: {v.secondary_name}{v.secondary_role ? ` (${v.secondary_role})` : ''}{v.secondary_email ? ` · ${v.secondary_email}` : ''}
                      </div>
                    )}

                    {editingNotes === `v-${v.id}` ? (
                      <div className="mt-2 flex gap-2">
                        <input
                          type="text"
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          placeholder="Add a note..."
                          autoFocus
                          onKeyDown={(e) => e.key === 'Enter' && saveNotes(v.id, 'vouchers')}
                          style={{
                            flex: 1, padding: '6px 10px', background: 'var(--paper-2)',
                            border: '1.25px solid var(--ink-4)', borderRadius: 3,
                            fontFamily: 'var(--hand)', fontSize: 13, color: 'var(--ink)', outline: 'none',
                          }}
                        />
                        <button onClick={() => saveNotes(v.id, 'vouchers')} className="nb-chip" style={{ cursor: 'pointer', borderColor: 'var(--nb-green)', color: 'var(--nb-green)' }}>save</button>
                        <button onClick={() => setEditingNotes(null)} className="nb-chip" style={{ cursor: 'pointer' }}>cancel</button>
                      </div>
                    ) : v.notes ? (
                      <div className="mt-2 flex items-start gap-1.5 cursor-pointer" onClick={() => { setEditingNotes(`v-${v.id}`); setNoteText(v.notes || ''); }}>
                        <MessageSquare size={11} style={{ color: 'var(--ink-4)', marginTop: 2 }} />
                        <span style={{ fontFamily: 'var(--hand)', fontSize: 13, color: 'var(--ink-3)', fontStyle: 'italic' }}>{v.notes}</span>
                      </div>
                    ) : null}
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--ink-4)', whiteSpace: 'nowrap' }}>
                      {v.claimed_at ? new Date(v.claimed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => moveToRequests(v.id)} title="Move to requests" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nb-accent)', padding: 4 }}>
                        <ArrowRightCircle size={13} />
                      </button>
                      <button onClick={() => { setEditingNotes(`v-${v.id}`); setNoteText(v.notes || ''); }} title="Add note" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', padding: 4 }}>
                        <MessageSquare size={13} />
                      </button>
                      <button onClick={() => deleteRequest(v.id, 'vouchers')} title="Unclaim voucher" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', padding: 4 }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {filteredVouchers.length === 0 && !loading && (
              <div className="text-center py-12">
                <p style={{ fontFamily: 'var(--hand)', fontSize: 16, color: 'var(--ink-3)' }}>
                  {filter ? 'No matching vouchers' : 'No vouchers claimed yet'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessRequestsAdmin;
